import React, { useState, useEffect, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { RotateCw, ZoomIn, ZoomOut, X, Check, RefreshCw } from "react-feather";
import { Button } from "semantic-ui-react";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#ffffff",
  boxShadow: 24,
  borderRadius: "12px",
  outline: "none",
  overflow: "hidden",
  maxWidth: "92vw",
  width: "480px",
};

const VIEWPORT_SIZE = 320;
const CROP_DIAMETER = 240;

export default function ProfilePictureEditorModal({
  open,
  imageSrc,
  fileName = "profile-picture.jpg",
  onClose,
  onSave,
  isSaving,
}) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0); // 0, 90, 180, 270
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageObj, setImageObj] = useState(null);

  const canvasRef = useRef(null);

  // Load image when imageSrc changes
  useEffect(() => {
    if (!imageSrc) {
      setImageObj(null);
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setImageObj(img);
      setZoom(1);
      setRotation(0);
      setOffset({ x: 0, y: 0 });
    };
    img.src = imageSrc;
  }, [imageSrc]);

  // Compute base cover scale
  const getBaseScale = useCallback(() => {
    if (!imageObj) return 1;
    const isRotated90or270 = rotation === 90 || rotation === 270;
    const width = isRotated90or270 ? imageObj.naturalHeight : imageObj.naturalWidth;
    const height = isRotated90or270 ? imageObj.naturalWidth : imageObj.naturalHeight;
    return Math.max(CROP_DIAMETER / width, CROP_DIAMETER / height);
  }, [imageObj, rotation]);

  // Redraw preview canvas
  useEffect(() => {
    if (!canvasRef.current || !imageObj) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const width = VIEWPORT_SIZE;
    const height = VIEWPORT_SIZE;

    canvas.width = width;
    canvas.height = height;

    ctx.clearRect(0, 0, width, height);

    // 1. Draw dark slate background
    ctx.fillStyle = "#1e293b";
    ctx.fillRect(0, 0, width, height);

    // 2. Draw transformed image
    const baseScale = getBaseScale();
    const currentScale = baseScale * zoom;
    const rad = (rotation * Math.PI) / 180;

    const centerX = width / 2 + offset.x;
    const centerY = height / 2 + offset.y;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(rad);
    ctx.drawImage(
      imageObj,
      (-imageObj.naturalWidth * currentScale) / 2,
      (-imageObj.naturalHeight * currentScale) / 2,
      imageObj.naturalWidth * currentScale,
      imageObj.naturalHeight * currentScale
    );
    ctx.restore();

    // 3. Draw circular crop mask overlay
    ctx.save();
    // Fill the outer mask with semi-transparent dark overlay
    ctx.fillStyle = "rgba(15, 23, 42, 0.65)";
    ctx.beginPath();
    ctx.rect(0, 0, width, height);
    ctx.arc(width / 2, height / 2, CROP_DIAMETER / 2, 0, Math.PI * 2, true);
    ctx.fill("evenodd");

    // Circular crop border guideline
    ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 6]);
    ctx.beginPath();
    ctx.arc(width / 2, height / 2, CROP_DIAMETER / 2, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }, [imageObj, zoom, rotation, offset, getBaseScale]);

  // Drag handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers for mobile/small screens
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - offset.x,
        y: touch.clientY - offset.y,
      });
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging || e.touches.length !== 1) return;
    const touch = e.touches[0];
    setOffset({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y,
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
    setOffset({ x: 0, y: 0 }); // reset offset on rotate to keep centered
  };

  const handleReset = () => {
    setZoom(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
  };

  const handleSaveCrop = () => {
    if (!imageObj || isSaving) return;

    // Create high-res 512x512 export canvas
    const exportSize = 512;
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = exportSize;
    exportCanvas.height = exportSize;
    const ctx = exportCanvas.getContext("2d");

    const scaleFactor = exportSize / CROP_DIAMETER;
    const baseScale = getBaseScale();
    const currentScale = baseScale * zoom * scaleFactor;
    const rad = (rotation * Math.PI) / 180;

    const centerX = exportSize / 2 + offset.x * scaleFactor;
    const centerY = exportSize / 2 + offset.y * scaleFactor;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(rad);
    ctx.drawImage(
      imageObj,
      (-imageObj.naturalWidth * currentScale) / 2,
      (-imageObj.naturalHeight * currentScale) / 2,
      imageObj.naturalWidth * currentScale,
      imageObj.naturalHeight * currentScale
    );
    ctx.restore();

    exportCanvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], fileName.replace(/\.[^/.]+$/, "") + ".jpg", {
            type: "image/jpeg",
          });
          onSave(file);
        }
      },
      "image/jpeg",
      0.92
    );
  };

  return (
    <Modal
      open={open}
      onClose={isSaving ? undefined : onClose}
      aria-labelledby="crop-modal-title"
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#111827" }}>
              Crop & Rotate
            </span>
          </div>
          {!isSaving && (
            <button
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                borderRadius: "50%",
              }}
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Viewport Area */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "#0f172a",
            padding: "16px 0",
            position: "relative",
          }}
        >
          <div
            style={{
              width: VIEWPORT_SIZE,
              height: VIEWPORT_SIZE,
              position: "relative",
              cursor: isDragging ? "grabbing" : "grab",
              userSelect: "none",
              touchAction: "none",
              borderRadius: "8px",
              overflow: "hidden",
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
          </div>

          <span
            style={{
              color: "#94a3b8",
              fontSize: "12px",
              marginTop: "8px",
            }}
          >
            Drag photo to reposition inside the circle
          </span>
        </div>

        {/* Controls */}
        <div style={{ padding: "16px 24px" }}>
          {/* Zoom Slider */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <ZoomOut size={16} color="#6b7280" />
            <input
              type="range"
              min="1"
              max="3"
              step="0.02"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              disabled={isSaving}
              style={{
                flex: 1,
                accentColor: "#2563eb",
                cursor: "pointer",
              }}
            />
            <ZoomIn size={16} color="#6b7280" />
          </div>

          {/* Action Toolbar */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              marginBottom: "6px",
            }}
          >
            <Button
              type="button"
              basic
              size="small"
              onClick={handleRotate}
              disabled={isSaving}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                borderRadius: "20px",
              }}
            >
              <RotateCw size={14} /> Rotate
            </Button>
            <Button
              type="button"
              basic
              size="small"
              onClick={handleReset}
              disabled={isSaving}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                borderRadius: "20px",
              }}
            >
              <RefreshCw size={14} /> Reset
            </Button>
          </div>
        </div>

        {/* Footer Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "10px",
            padding: "12px 20px 16px",
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "#f9fafb",
          }}
        >
          <Button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            style={{ borderRadius: "6px" }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            primary
            onClick={handleSaveCrop}
            loading={isSaving}
            disabled={isSaving}
            style={{
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: "#2563eb",
            }}
          >
            <Check size={15} /> Save as profile picture
          </Button>
        </div>
      </Box>
    </Modal>
  );
}
